import type { SearchClient } from 'algoliasearch';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { waitForAndGetFlagValuesSaga } from '@/domains/core/flags/sagas/waitForAndGetFlagValues.saga';
import { getOrCreateVisitorId } from '@/domains/core/tracking/utils/visitorId';
import { authSelector } from '@/domains/customerManagement/customerManagement.selectors';
import {
  ABTests as AlgoliaABTests,
  StringFeatureFlags as AlgoliaFeatureFlags,
} from '@/domains/productDiscovery/algolia/constants';
import { AlgoliaSearchInsights } from '@/domains/productDiscovery/algolia/searchInsights/AlgoliaSearchInsights';
import {
  SET_SEARCH_INPUT,
  type SetSearchInputAction,
} from '@/domains/productDiscovery/HeaderLeonidas/modules/searchBar/HeaderLeonidas.searchBar.actions';
import {
  productsSearchClientInstance,
  searchMultiple,
} from '@/domains/productDiscovery/HeaderLeonidas/modules/searchBar/services/algolia/proxy';
import { StringABTests as ListingsStringABTests } from '@/domains/productDiscovery/Listings/constants';
import { StringFeatureFlags as SearchFeatureFlags } from '@/domains/productDiscovery/Listings/pages/Search/constants';
import { fetchSpDataSearchBar } from '@/domains/sellerAdsManagement/SponsoredProducts/services/fetchSpDataByQuery';
import { toAlgoliaHitWithSponsoredData } from '@/productDiscovery/HeaderLeonidas/modules/productSuggestions/modules/searchResults/utils/toAlgoliaHitWithSponsoredData';
import { SponsoredProduct } from '@/productDiscovery/Listings/sponsoredProducts/interfaces/sponsoredProduct';
import { getAbtParameterFromSaga } from '@/productDiscovery/SponsoredProducts/utils/getAbtParameter';

import type { ProductsSuggestionSearchResult } from '../../interfaces/algoliaResponse';
import {
  fetchSearchBarProducts,
  fetchSearchBarProductsFailed,
  fetchSearchBarProductsSuccess,
} from './actions';
import {
  PRODUCTS_SUGGESTIONS_INDEX,
  SPONSORED_PRODUCTS_SUGGESTIONS_INDEX,
} from './constants';
import { buildSuggestionsProductsSearchParameters } from './services/algolia/searchParameters';

function* fetchSearchBarSuggestionsProducts(
  action: SetSearchInputAction,
): Generator<any, any, any> {
  try {
    if (!action?.payload?.inputValue) {
      return;
    }

    const { payload } = action;
    const { inputValue: query } = payload;

    yield put(fetchSearchBarProducts());

    const flags = yield call(waitForAndGetFlagValuesSaga);
    const rankingEnabled = Boolean(flags[AlgoliaABTests.RANKING]);

    const rerankingFF = flags[SearchFeatureFlags.RERANKING_SEARCH];
    const rerankingAbTest = Boolean(
      flags[ListingsStringABTests.RERANKING_SEARCH_AND_ML],
    );
    const abt = getAbtParameterFromSaga(flags);
    const SEARCH_QUERY_MIN_SIZE = 3;
    const sponsoredProductsData =
      query.length >= SEARCH_QUERY_MIN_SIZE
        ? yield call(fetchSpDataSearchBar, query, {}, abt)
        : null;
    const [sponsoredProducts, sponsoredProductsPositions]: [
      SponsoredProduct[],
      number[],
      boolean,
    ] = yield handleSponsoredProducts(
      sponsoredProductsData ? sponsoredProductsData.sponsoredProducts : [],
      sponsoredProductsData
        ? sponsoredProductsData.sponsoredProductPositions
        : [],
    );
    const { userId } = yield select(authSelector);
    const visitorId = yield call(getOrCreateVisitorId);
    const userToken = userId
      ? yield call(AlgoliaSearchInsights.calculateUserIdHash, String(userId))
      : visitorId;

    const searchEngineProxyEnabled =
      flags[AlgoliaFeatureFlags.ALGOLIA_SEARCH_ENGINE_PROXY];

    const searchParameters = buildSuggestionsProductsSearchParameters(query, {
      rerankingAbTest,
      reranking: rerankingFF,
      ranking: rankingEnabled,
      sponsoredProducts,
    });

    const client: SearchClient = yield call(
      productsSearchClientInstance,
      searchEngineProxyEnabled,
    );
    const productSuggestionsResults: ProductsSuggestionSearchResult[] =
      yield call(searchMultiple, client, searchParameters, userToken);
    const productSuggestions =
      productSuggestionsResults[PRODUCTS_SUGGESTIONS_INDEX];

    const sponsoredProductSuggestions: ProductsSuggestionSearchResult =
      productSuggestionsResults[SPONSORED_PRODUCTS_SUGGESTIONS_INDEX];
    const spSuggestions = {
      ...sponsoredProductSuggestions,
      hits:
        sponsoredProducts.length > 0
          ? toAlgoliaHitWithSponsoredData(
              sponsoredProductSuggestions,
              sponsoredProducts,
              sponsoredProductsPositions,
            )
          : [],
    };
    yield put(
      fetchSearchBarProductsSuccess({
        ...productSuggestions,
        hits: [
          ...spSuggestions.hits,
          ...productSuggestions.hits.slice(
            0,
            productSuggestions.hits.length - spSuggestions.hits.length,
          ),
        ],
      } as ProductsSuggestionSearchResult),
    );
  } catch (error) {
    yield put(fetchSearchBarProductsFailed());
  }
}

function handleSponsoredProducts(
  rawSponsoredProducts?: SponsoredProduct[],
  rawSponsoredProductsPositions?: number[],
): [SponsoredProduct[], number[]] {
  if (rawSponsoredProducts) {
    const sponsoredProductsPositions = rawSponsoredProductsPositions || [];
    const processedSponsoredProducts = (rawSponsoredProducts ?? []).slice(0, 2);

    return [processedSponsoredProducts || [], sponsoredProductsPositions];
  }
  return [[], []];
}

function* watchFetchProducts() {
  yield takeLatest(SET_SEARCH_INPUT, fetchSearchBarSuggestionsProducts);
}

export default function* rootSearchBarSuggestionsProducts() {
  yield fork(watchFetchProducts);
}
